/* @flow */
import moment from 'moment';
import { savePriceSummaryLinkInformation } from '../utility/comparator';

export default ngModule => {

  ngModule.factory('AonApplicationService', function ($q, AonConfig, AonApi, PrimaryServiceNeeds, $state, $log, $location) {

    /**
     * Get value from list of key, value pairs given a key
     * @param key
     * @param pairs
     * @returns {*}
     */
    function getPairValue(key, pairs) {
      for (var [k, v] of pairs) {
        if (k === key) {
          return v;
        }
      }

      return undefined;
    }

    /**
     * Get age based on date of birth
     *
     * Approximate, assumes 365 days per year
     *
     * @param dob
     * @returns {number}
     */
    function getAge(dob) {
      if (!dob) {
        return null;
      }
      const now = new Date(),
        [year, month, day] = dob.split('-'),
        bday = new Date(year, month - 1, day);

      return Math.floor((now - bday) / 1000 / 60 / 60 / 24 / 365);
    }

    function getRebateTierChoices(application) {
      const key = application.policy.membership_type === 0 ? 'singles' : 'family';
      return AonConfig.formChoices.rebate_tiers[key];
    }

    function getMedicareCardColours() {
      return AonConfig.formChoices.medicare_colours_choices;
    }

    class AonApplication {

      constructor(data) {
        _.extend(this, data);
        this.displayValues = this.getSummaryDetails();
      }

      getSummaryDetails() {
        let details = {};
        for (let field of ['policy.cover_type', 'policy.membership_type', 'primary_address.state']) {
          const [key1, key2] = field.split(".");
          details[key2] = getPairValue(this[key1][key2], AonConfig.formChoices[key2]);
        }

        details.age = this.personal.age || getAge(this.personal.dob);

        if (this.policy.federal_rebate) {
          const [,rebateText] = getRebateTierChoices(this)[this.policy.rebate_tier];
          details.apply_for_rebate = `Govt. Rebate Applied (${rebateText})`;
        } else {
          details.apply_for_rebate = 'No Govt. Rebate Applied';
        }

        return details;
      }

      /**
       * Does this applicant want packages that include ancillary products?
       * @returns {boolean}
       */
      requiresAncillaryProducts() {
        return this.policy.cover_type === 1 || this.policy.cover_type === 2;
      }

      /**
       * Does this applicant want packages that include hospital products?
       * @returns {boolean}
       */
      requiresHospitalProducts() {
        return this.policy.cover_type === 0 || this.policy.cover_type === 2;
      }

      /*
      SINGLE = 0
      COUPLE = 1
      FAMILY = 2
      FAMILY_SINGLE_PARENT = 3
      CHILDREN_ONLY = 4
      EXTENDED_FAMILY = 5
      SINGLE_ANY_DEPENDANTS = 6
      COUPLE_ANY_DEPENDANTS = 7
      */
      hasPartner() {
        return this.policy.membership_type === 1 || this.policy.membership_type === 2 ||
            this.policy.membership_type === 5 || this.policy.membership_type === 7;
      }

      isSingle() {
        return this.policy.membership_type === 0;
      }

      /**
       * Return true if membership type indicates the applicant should have dependants
       */
      requiresDependants() {
        return this.policy.membership_type > 1;
      }

      motivationChoices() {
        const isCovered = _.get(this, 'personal.previous_fund.has_previous_fund', false);
        return AonConfig.formChoices.motivation[isCovered ? 'covered' : 'not_covered'];
      }

      motivationSubChoices() {
        const primaryMotivation = _.get(this, 'policy.motivation');
        const primaryReason = this.motivationChoices().filter(option => option[0] === primaryMotivation);
        if (primaryReason.length) {
          const subReasons = primaryReason[0][2];
          return subReasons && subReasons.length ? subReasons : null;
        }
        return null;
      }
    }

    class AonApplicationService {

      constructor() {
        const defaultPrimaryServiceNeed = PrimaryServiceNeeds.getDefaultPrimaryNeed();

        this.application = {
          source: AonConfig.jsConstants.sourceOfApplication,
          personal: {},
          contact: {},
          partner: {},
          primary_address: {},
          has_secondary_address: false,
          secondary_address: {},
          number_of_dependants: null,
          dependants: [],
          policy: {
            primary_service_need: defaultPrimaryServiceNeed ? defaultPrimaryServiceNeed.code : null,
          },
          comparison_product: {
          },
          payment: {
            benefits_same_account: true,
          },
        };
        this.price = {};
        this.product = {};
        this.submitResponse = {};
        this.competition = {
          optIn: false,
          optOut: false
        };
        this.debug = $location.search().hasOwnProperty('debug');
      }

      /**
       * Set to true to allow debug overlay of price calculations
       * @param debugOn
       */
      setDebug(debugOn) {
        this.debug = debugOn;
      }

      /**
       * Return true if debug mode is on
       */
      isDebug() {
        return this.debug;
      }

      /**
       * Expose the getAge function, for use in calculating partner age
       * @param dob (string) date of birth
       * @returns {number} age
       */
      calculateAge(dob) {
        return getAge(dob);
      }

      /**
       * Get the applicant. Note that to save any changes you must call saveApplication()
       *
       * @returns {Object}
       */
      getApplication() {
        const deferred = $q.defer();

        // Don't pass reference through; copy it. This stops changes being propogated when we don't want it. All changes
        // must go through saveApplication
        let copy = new AonApplication(_.cloneDeep(this.application));
        deferred.resolve(copy);

        return deferred.promise;
      }

      getProduct(clone = true) {
        return clone ? _.cloneDeep(this.product) : this.product;
      }

      /**
       * Save changes to the application on the client side. For server side see submitApplication
       * @param applicant
       */
      saveApplication(application) {
        _.assign(this.application, application);
        savePriceSummaryLinkInformation(application);
      }

      /**
       * Save the response object returned after submission to gateway
       * @param response
       */
      saveResponse(response) {
        this.submitResponse = response;
      }

      /**
       * Get the application submission response object
       */
      getResponse() {
        return this.submitResponse;
      }

      /**
       * Set the price to chosen
       *
       * @param {Object} price
       */
      setPrice(price) {
        this.price = price;
      }

      setProduct(product) {
        this.product = product;
      }

      /**
       * Has a price been set for this application?
       * @returns {boolean}
       */
      hasPrice() {
        return this.price && this.price.id !== undefined;
      }

      /**
       * Has a state (i.e. VIC) been set for this application (use to test if step 1 completed)
       * @returns {boolean}
       */
      hasAddressAndState() {
        return this.application.primary_address && this.application.primary_address.state !== undefined;
      }

      /**
       * Set the competition entry as opted-in
       */
      setCompetitionOptIn() {
        this.competition.optIn = true;
        trackEvent("COMPARATOR_COMPETITION_ENTRY", 0);
      }

      /**
       * Set the competition entry as opted-out
       */
      setCompetitionOptOut() {
        this.competition.optOut = true;
      }

      setCustomerReference(ref) {
        this.application.customer_reference = ref;
      }

      generateCustomerReference(forceRegenerate = false) {
        const deferred = $q.defer();
        if (!forceRegenerate && this.application.customer_reference) {
          deferred.resolve(this.application.customer_reference);
        } else {
          let url = `funds/customerreference`;
          if (this.application.zoho_id) {
            url = `${url}?zoho_id=${this.application.zoho_id}`;
          }
          AonApi.get(url).then(result => {
            this.setCustomerReference(result.data.customer_reference);
            deferred.resolve(result.data.customer_reference);
          }, error => deferred.reject(error));
        }
        return deferred.promise;
      }

      /**
       * Return true if the applicant has opted in or out
       * to entering the competition
       */
      getCompetitionHasOpted() {
        return this.competition.optIn || this.competition.optOut;
      }

      getFieldSets() {
        if (!this.hasPrice()) {
          $state.transitionTo("comparator.step2");
          $log.debug("Expected a price, found none. Redirecting.");
          return;
        }
        const deferred = $q.defer();
        const success = (response) => {
            deferred.resolve(response.data);
        },
        error = (data) => {
          deferred.reject();
        };
        AonApi.get(`funds/${this.price.fund.id}/fieldsets?price_id=${this.price.id}`).then(success, error);

        return deferred.promise;
      }

      submitApplication(application, postDataTransformers = [], applicationId = undefined) {
        this.submitResponse = null;
        const deferred = $q.defer();

        const success = (response) => {
          this.saveResponse(response);
          deferred.resolve(response.data);
        };

        const error = (response) => {
          this.saveResponse(response);
          deferred.reject(response);
          if (response.status !== 400) {
            $log.error("Unexpected response from server", response);
          }
        };

        let postData = _.cloneDeep(application);
        postData.source = AonConfig.jsConstants.sourceOfApplication;

        const dateToString = (date) => {
          const nativeDate = moment.isMoment(date) ? moment(date).toDate() : date;
          return [
            nativeDate.getFullYear(),
            nativeDate.getMonth() + 1,
            nativeDate.getDate(),
          ].join("-");
        };

        const convertDateFields = ['payment.debit_date', 'payment.join_date'];
        for (let i = 0; i < convertDateFields.length; i++) {
          const date = _.get(postData, convertDateFields[i], false);
          if (date !== false) {
            _.set(postData, convertDateFields[i], dateToString(date));
          }
        }

        postData['direct_debit_discount'] = _.get(this.product, 'apply_direct_debit_discount', false);

        postData['product'] = {
          corporate: AonConfig.corporate,
          total_premium: _.get(this.product, 'selectedPrice.pricing.available.monthly_adjusted_price_dd_discount.premium'),
        };

        postData['application_id'] = applicationId

        const corporate_id = encodeURIComponent(AonConfig.corporate.id),
              price_id = encodeURIComponent(this.product.selectedPrice.id);

        delete(postData.displayValues);
        if(!postData.has_secondary_address) {
          delete(postData.secondary_address)
        }
        // Apply any other data transformations
        for (let fn of postDataTransformers) {
          postData = fn(_.assign({}, postData));
        }

        this.saveApplication(application);
        // Now attempt the gateway submission

        // Testing - this should NOT be cached anyway - but
        const cacheBuster = Math.random().toString() + (new Date().getTime());
        AonApi.post(`application?price_id=${price_id}&corporate_id=${corporate_id}&bust=${cacheBuster}`, postData).then(success, error);

        return deferred.promise;
      }

      saveApplicationInZoho(application) {
        const deferred = $q.defer();

        const success = (response) => {
          deferred.resolve(response.data);
        };

        const error = (response) => {
          deferred.reject(response);
          if (response.status !== 400) {
            $log.error("Unexpected response from server", response);
          }
        };

        let postData = _.cloneDeep(application);
        postData.source = AonConfig.jsConstants.sourceOfApplication;

        const dateToString = (date) => [date.getFullYear(),date.getMonth()+1,date.getDate()].join("-");
        const convertDateFields = ['payment.debit_date', 'payment.join_date'];

        for (let dateField of convertDateFields) {
          let date = postData[dateField];
          if (date !== undefined) {
            postData[dateField] = dateToString(date);
          }
        }

        postData['direct_debit_discount'] = _.get(this.product, 'apply_direct_debit_discount', false);

        postData['product'] = {
          corporate: AonConfig.corporate,
          total_premium: _.get(this.product, 'selectedPrice.pricing.available.monthly_adjusted_price_dd_discount.premium'),
        };

        const price_id = encodeURIComponent(this.product.selectedPrice.id);

        delete(postData.displayValues);
        if(!postData.has_secondary_address) {
          delete(postData.secondary_address)
        }

        this.saveApplication(application);

        // Testing - this should NOT be cached anyway - but
        const cacheBuster = Math.random().toString() + (new Date().getTime());
        return AonApi.post(`save_application?price_id=${price_id}&bust=${cacheBuster}`, postData)
          .then(response => response.data)
          .catch(
            response => {
              if (response.status !== 400) {
                $log.error("Unexpected response from server", response);
              }
              return response;
            }
          );
      }

      getRebateTierChoices(application) {
        return getRebateTierChoices(application);
      }

      getMedicareColourChoices() {
        return getMedicareCardColours();
      }

      getRebateTierText(application) {
        let caption = AonConfig.corporate.questionConfig.step_1_rebate_tier_caption;
        let description = AonConfig.corporate.questionConfig.step_1_rebate_tier_description;
        if (typeof application.policy !== 'undefined' &&
            typeof application.policy.membership_type !== 'undefined' &&
            application.policy.membership_type > 0) {
          caption = AonConfig.corporate.questionConfig.step_1_rebate_tier_combined_caption;
          description = AonConfig.corporate.questionConfig.step_1_rebate_tier_combined_description;
        }
        return {
          caption: caption,
          description: description
        };
      }

      // TODO: remove this function in the future
      trackEvent(event, fund_id=null) {
        // AonApi.post('tracking', {event, fund_id});
      }

    }

    return new AonApplicationService();

  });


};



// WEBPACK FOOTER //
// ./src/services/application-service.js